import { format } from "date-fns";
import { enUS, ru } from "date-fns/locale";
import type { Locale } from "date-fns";
export type DateTimeFormatType = "usa" | "europe";

const normalizeDateTimeFormat = (
  value: string | undefined
): DateTimeFormatType => {
  return value === "usa" ? "usa" : "europe";
};

/**
 * Short date format for "last searches" display.
 * Europe: "dd MMM" (28 Feb) | USA: "MMM dd" (Feb 28)
 */
export const getShortDateFormat = (dtf: DateTimeFormatType): string => {
  return dtf === "usa" ? "MMM dd" : "dd MMM";
};

/**
 * Display date format with full month name (for calendar headers).
 * Europe: "d MMMM" (28 February) | USA: "MMMM d" (February 28)
 */
export const getDisplayDateFormat = (dtf: DateTimeFormatType): string => {
  return dtf === "usa" ? "MMMM d" : "d MMMM";
};

/**
 * Legacy's `FormatDate::TYPE_DATE_TEXT` — the spelled-out month a service card
 * prints instead of the numeric profile pattern.
 * Europe: "d MMMM yyyy" (9 September 2026) | USA: "MMMM d, yyyy" (September 9, 2026)
 *
 * The tokens are the date-fns twins of the Carbon patterns legacy renders with
 * (`'j F Y'` / `'F j, Y'`, FormatDate.php:219-232) — day WITHOUT a leading zero.
 * Deliberately not the `OUTPUT_AIRPICKER` row of that same table
 * (`'dd MMMM yyyy'` / `'MMMM dd, yyyy'`): airpicker pads the day, so emitting it
 * from the backend would print "09 September 2026" on a page whose legacy twin
 * says "9 September 2026".
 */
export const getTextDateFormat = (dtf: DateTimeFormatType): string => {
  return dtf === "usa" ? "MMMM d, yyyy" : "d MMMM yyyy";
};

/**
 * The narrow-column twin of `getTextDateFormat` — "Sep 19, 26" / "19 Sep 26".
 * A table cell has no room for "September 19, 2026", and the month name is the
 * part worth paying for: "09/07/2026" is two different days depending on who
 * wrote it, and a column header has nowhere to say which one it means.
 */
export const getShortTextDateFormat = (dtf: DateTimeFormatType): string => {
  return dtf === "usa" ? "MMM d, yy" : "d MMM yy";
};

export const formatTimeByDateTimeFormat = (
  value: string | null | undefined,
  dateTimeFormat: string | undefined,
  fallback: string = "--:--"
): string => {
  if (!value || typeof value !== "string") return fallback;

  const date = new Date(value);
  if (isNaN(date.getTime())) return fallback;

  const normalized = normalizeDateTimeFormat(dateTimeFormat);
  const isUsa = normalized === "usa";

  return date.toLocaleTimeString(
    isUsa ? "en-US" : "ru-RU",
    isUsa
      ? {
            hour: "numeric",
            minute: "2-digit",
            hour12: true,
        }
      : {
            hour: "2-digit",
            minute: "2-digit",
            hour12: false,
        }
  );
};

/**
 * A wall-clock "HH:mm" as the profile shows time: "14:30" stays as is for europe and
 * becomes "2:30 PM" for usa. Only the label changes; pickers keep "HH:mm" as the value.
 */
export const formatClockTime = (hhmm: string, dateTimeFormat: string | undefined): string => {
  const m = /^(\d{1,2}):(\d{2})$/.exec(hhmm);
  if (!m || normalizeDateTimeFormat(dateTimeFormat) !== "usa") return hhmm;
  const hours = Number(m[1]);
  const suffix = hours < 12 ? "AM" : "PM";
  return `${hours % 12 || 12}:${m[2]} ${suffix}`;
};

/** Fallback pattern when the profile carries none — matches UserContext. */
export const DEFAULT_DATE_FORMAT = "dd.MM.yyyy";

/**
 * Date-only pattern from the user's profile: "MM/dd/yyyy" (usa) or "dd.MM.yyyy"
 * (europe). The backend emits it from FormatDate::getDatePattern(OUTPUT_AIRPICKER),
 * whose tokens are date-fns compatible. Module-level twin of `useUser().dateFormat`
 * for code that runs outside a component (column builders, mappers) — same source,
 * see resolveMoneyFormat in lib/money.ts.
 */
export const resolveUserDateFormat = (): string => {
  if (typeof window === "undefined") return DEFAULT_DATE_FORMAT;
  return window.__USER__?.dateFormat || DEFAULT_DATE_FORMAT;
};

/** Module-level twin of `useUser().dateTimeFormat` — drives the clock (24h vs am/pm). */
export const resolveDateTimeFormat = (): DateTimeFormatType =>
  normalizeDateTimeFormat(
    typeof window === "undefined" ? undefined : window.__USER__?.dateTimeFormat
  );

export interface ProfileDateParts {
  weekday?: "short" | "long"
  month?: "short" | "long"
  year?: boolean
}

/**
 * A spelled-out date in the order the profile asks for: "Mon, 2 Nov 2026" (europe) or
 * "Mon, Nov 2, 2026" (usa) — the date-fns twin of `FormatDate`'s text patterns ('j F Y' / 'F j, Y').
 */
export const profileDatePattern = (dtf: DateTimeFormatType, parts: ProfileDateParts = {}): string => {
  const weekday = parts.weekday ? `${parts.weekday === "long" ? "EEEE" : "EEE"}, ` : "";
  const month = parts.month === "short" ? "MMM" : "MMMM";
  if (dtf === "usa") return `${weekday}${month} d${parts.year ? ", yyyy" : ""}`;
  return `${weekday}d ${month}${parts.year ? " yyyy" : ""}`;
};

export const formatProfileDate = (date: Date, parts: ProfileDateParts = {}, dtf: DateTimeFormatType = resolveDateTimeFormat()): string =>
  format(date, profileDatePattern(dtf, parts), { locale: resolveDateFnsLocale() });

/** "2 – 3 November 2026" / "November 2 – November 3, 2026": the year once when both ends share it. */
export const formatProfileDateRange = (
  start: Date,
  end: Date | null | undefined,
  parts: ProfileDateParts = {},
  dtf: DateTimeFormatType = resolveDateTimeFormat()
): string => {
  if (!end || end.getTime() === start.getTime()) return formatProfileDate(start, parts, dtf);
  const head = formatProfileDate(start, { ...parts, year: parts.year && start.getFullYear() !== end.getFullYear() }, dtf);
  return `${head} – ${formatProfileDate(end, parts, dtf)}`;
};

/**
 * The page's display language. `react-hybrid.php:245` already normalises it to
 * exactly "ru" or "en", so this needs no slicing — unlike
 * `document.documentElement.lang`, which the same layout emits as "en-US"
 * (:86). Only ever read for a MONTH NAME: the numeric patterns above are
 * language-free, which is why nothing needed this before.
 */
export const resolveUserLang = (): "ru" | "en" => {
  if (typeof window === "undefined") return "en";
  return window.__USER__?.lang === "ru" ? "ru" : "en";
};

/**
 * The date-fns locale that spells the month. Same pick the booking search forms
 * make (`pages/booking/components/date-picker.tsx:159`); `enUS` is date-fns'
 * own default, so passing it changes nothing but keeps the branch honest.
 */
export const resolveDateFnsLocale = (): Locale =>
  resolveUserLang() === "ru" ? ru : enUS;

/**
 * `FormatDate::do()` runs every `TYPE_DATE_TEXT*` result through
 * `mb_convert_case(..., MB_CASE_TITLE)` (FormatDate.php:273-284), so the legacy
 * cards read "9 Сентября 2026" where date-fns' `ru` locale gives the
 * grammatically correct lowercase "сентября". The React card sits next to the
 * classic one during the migration, so it matches legacy rather than being
 * quietly righter than it. A no-op in English, where the month is already
 * capitalised.
 */
const titleCase = (value: string): string =>
  value.replace(/\p{L}+/gu, (word) => word.charAt(0).toUpperCase() + word.slice(1));

/**
 * Parses an API date/datetime ("yyyy-MM-dd", "yyyy-MM-dd HH:mm:ss" or ISO) as a
 * LOCAL wall-clock date. `new Date("2026-08-26")` is parsed as UTC midnight and
 * renders as the previous day west of Greenwich, and a space-separated datetime
 * is implementation-defined — so the parts are read by hand. Anything not
 * ISO-shaped stays unparsed rather than being guessed at: "11/02/2026" means two
 * different days depending on who wrote it.
 */
export const parseApiDate = (
  value: string | Date | null | undefined
): Date | undefined => {
  if (!value) return undefined;
  if (value instanceof Date) return isNaN(value.getTime()) ? undefined : value;

  const match = /^(\d{4})-(\d{2})-(\d{2})(?:[T ](\d{2}):(\d{2})(?::(\d{2}))?)?/.exec(
    value
  );
  if (!match) return undefined;

  const [, y, m, d, hh, mm, ss] = match;
  const parsed = new Date(
    Number(y),
    Number(m) - 1,
    Number(d),
    Number(hh ?? 0),
    Number(mm ?? 0),
    Number(ss ?? 0)
  );
  return isNaN(parsed.getTime()) ? undefined : parsed;
};

/**
 * Renders an API date in the user's profile format. Pass `dateFormat` from
 * `useUser()` inside components; module-level callers may omit it.
 */
export const formatUserDate = (
  value: string | Date | null | undefined,
  dateFormat: string = resolveUserDateFormat(),
  fallback: string = "—"
): string => {
  const date = parseApiDate(value);
  if (!date) return typeof value === "string" && value ? value : fallback;

  return format(date, dateFormat || DEFAULT_DATE_FORMAT);
};

/**
 * A month, named — "September 2026" / "сентябрь 2026". For values that stand for
 * a whole month rather than a day: rendering the first of the month in the user's
 * day format reads as a date and invites comparing it with real ones.
 */
export const formatUserMonth = (
  value: string | Date | null | undefined,
  fallback: string = "—"
): string => {
  const date = parseApiDate(value);
  if (!date) return typeof value === "string" && value ? value : fallback;

  return format(date, "LLLL yyyy", { locale: resolveDateFnsLocale() });
};

/**
 * Date + clock in the user's formats — "26.08.2026 14:30" / "08/26/2026 2:30 PM".
 * The time is dropped when the value carries none. Server timestamps are read as
 * wall-clock: no offset is applied, matching how the rest of the app shows them.
 */
export const formatUserDateTime = (
  value: string | Date | null | undefined,
  dateFormat: string = resolveUserDateFormat(),
  dateTimeFormat: DateTimeFormatType = resolveDateTimeFormat(),
  fallback: string = "—"
): string => {
  const date = parseApiDate(value);
  if (!date) return typeof value === "string" && value ? value : fallback;

  const datePart = format(date, dateFormat || DEFAULT_DATE_FORMAT);
  const hasTime = typeof value === "string" ? /[T ]\d{2}:\d{2}/.test(value) : true;
  if (!hasTime) return datePart;

  return `${datePart} ${format(date, dateTimeFormat === "usa" ? "h:mm a" : "HH:mm")}`;
};

/**
 * The spelled-out twin of `formatUserDate` — "9 September 2026" /
 * "September 9, 2026" / "9 Сентября 2026". Legacy's `TYPE_DATE_TEXT`, which every
 * service card uses for the dates of the travel itself (hotel:298-299,
 * fly:617-618, car:59, transfer:49, railway:263, default:109) while leaving
 * deadlines and money dates on the numeric pattern.
 *
 * Europe/USA is taken from `dateTimeFormat`, the raw `usa|europe` flag the
 * layout already ships (react-hybrid.php:248) — not sniffed out of the numeric
 * `dateFormat` string. Both come from the same `$user->dateTimeFormat` column,
 * and `getShortDateFormat` / `getDisplayDateFormat` above already branch this way.
 */
export const formatUserDateText = (
  value: string | Date | null | undefined,
  dateTimeFormat: DateTimeFormatType = resolveDateTimeFormat(),
  fallback: string = "—"
): string => {
  const date = parseApiDate(value);
  if (!date) return typeof value === "string" && value ? value : fallback;

  return titleCase(
    format(date, getTextDateFormat(dateTimeFormat), { locale: resolveDateFnsLocale() })
  );
};

/**
 * `formatUserDateText` cut down to a table cell — "Sep 19, 26" / "19 Сент. 26".
 * Title-cased for the same reason as the long form: the legacy pages it sits
 * beside spell the month that way.
 */
export const formatUserDateShortText = (
  value: string | Date | null | undefined,
  dateTimeFormat: DateTimeFormatType = resolveDateTimeFormat(),
  fallback: string = "—"
): string => {
  const date = parseApiDate(value);
  if (!date) return typeof value === "string" && value ? value : fallback;

  return titleCase(
    format(date, getShortTextDateFormat(dateTimeFormat), {
      locale: resolveDateFnsLocale(),
    })
  );
};

/**
 * Legacy's `TYPE_DATE_TEXT_WITH_TIME` (`'j F Y H:i'` / `'F j, Y h:i A'`): the
 * textual date with the clock after it. Used where legacy uses it — the car
 * card's pickup/dropoff and the flight card's void deadline (fly:1194) — and
 * NOT for the pre-book deadline, which legacy keeps numeric (fly:739).
 *
 * Read as wall clock like `formatUserDateTime`: the server already resolved the
 * stamp into the site timezone.
 */
export const formatUserDateTextTime = (
  value: string | Date | null | undefined,
  dateTimeFormat: DateTimeFormatType = resolveDateTimeFormat(),
  fallback: string = "—"
): string => {
  const date = parseApiDate(value);
  if (!date) return typeof value === "string" && value ? value : fallback;

  const datePart = formatUserDateText(date, dateTimeFormat, fallback);
  const hasTime = typeof value === "string" ? /[T ]\d{2}:\d{2}/.test(value) : true;
  if (!hasTime) return datePart;

  return `${datePart} ${format(date, dateTimeFormat === "usa" ? "h:mm a" : "HH:mm")}`;
};

/** Placeholder mirroring the user's pattern: "MM/dd/yyyy" → "MM/DD/YYYY". */
export const getDatePlaceholder = (
  dateFormat: string = resolveUserDateFormat()
): string => (dateFormat || DEFAULT_DATE_FORMAT).toUpperCase();
